import {
  TirageHistory,
  useCreateTirageHistory,
  useDeleteTirageHistory,
  useEditTirageHistory,
  useGetHistoryOfTirage,
  useUpdateTirageHistorySequence,
} from "@/api/tirages/tirage-history.api";
import * as AF from "@/components/form-items/AbstractForm";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  tirageHistoriqueHistoriqueNom: z.string(),
  tirageHistoriqueHistoriqueVille: z.string(),
  tirageHistoriqueHistoriqueDates: z.string(),
  tirageHistoriqueHistoriqueCollectionParticuliere: z.boolean(),
});

type TirageHistoryForm = z.infer<typeof formSchema>;

type CreateOrEditTirageHistoryProps = {
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  tirageHistory?: TirageHistory;
  tirageId: string;
};

export const CreateOrEditTirageHistory = ({
  disabled = false,
  onOpenChange,
  open,
  tirageHistory,
  tirageId,
}: CreateOrEditTirageHistoryProps) => {
  const isEdit = tirageHistory !== undefined;

  const updater = useEditTirageHistory();
  const creator = useCreateTirageHistory();
  const deleter = useDeleteTirageHistory();

  const { t } = useTranslation();

  const form = useForm<TirageHistoryForm>({
    defaultValues: isEdit
      ? tirageHistory
      : {
          tirageHistoriqueHistoriqueCollectionParticuliere: false,
          tirageHistoriqueHistoriqueDates: "",
          tirageHistoriqueHistoriqueNom: "",
          tirageHistoriqueHistoriqueVille: "",
        },
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (tirageHistory) {
      form.reset(tirageHistory);
    }
  }, [tirageHistory, form]);

  function onSubmit(values: TirageHistoryForm) {
    if (isEdit && tirageHistory) {
      updater.mutate(
        {
          historyId: tirageHistory.id,
          tirageId: tirageHistory.tirageId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }

    creator.mutate(
      { history: values, tirageId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }

  function onDelete() {
    if (!isEdit || !tirageHistory) return;
    deleter.mutate(
      { tirageId: tirageHistory.tirageId, historyId: tirageHistory.id },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text name="tirageHistoriqueHistoriqueNom" />
              <AF.Text name="tirageHistoriqueHistoriqueVille" />
              <AF.Text name="tirageHistoriqueHistoriqueDates" />
              <AF.Checkbox name="tirageHistoriqueHistoriqueCollectionParticuliere" />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton type="submit" className="flex-1" disabled={disabled} />
              ) : (
                <SaveButton type="submit" className="flex-1" disabled={disabled} />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export const TirageHistoryTable = ({ tirageId, disabled = false }: { tirageId?: string, disabled?: boolean }) => {
  const { t } = useTranslation();

  const { data = [] } = useGetHistoryOfTirage(tirageId);

  const [rows, setRows] = useState<TirageHistory[]>([]);

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<TirageHistory>();

  const sequenceMutator = useUpdateTirageHistorySequence();

  function handleUpdate(tirageHistory: TirageHistory) {
    historyToUpdate.current = tirageHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<TirageHistory>[] => [
      {
        size: 1000,
        header: t("tab_tirageHistoriqueHistoriqueNom"),
        accessorKey: "tirageHistoriqueHistoriqueNom",
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueVille"),
        accessorKey: "tirageHistoriqueHistoriqueVille",
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueDates"),
        accessorKey: "tirageHistoriqueHistoriqueDates",
      },
      {
        header: t("tab_tirageHistoriqueHistoriqueCollectionParticuliere"),
        cell: (v) => (
          <BooleanBadge
            value={
              v.row.original.tirageHistoriqueHistoriqueCollectionParticuliere
            }
          />
        ),
      },
    ],
    [t],
  );

  useEffect(() => {
    setRows(data);
  }, [data]);

  const handleRowDragged = (newData: TirageHistory[]) => {
    if (!tirageId) return;
    setRows(newData);
    const newSequence = newData.map((d) => d.id);
    sequenceMutator.mutate({
      newSequence,
      tirageId,
    });
  };

  if (!tirageId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <CreateOrEditTirageHistory
        tirageId={tirageId}
        tirageHistory={historyToUpdate.current}
        open={openEditForm}
        onOpenChange={setOpenEditForm}
        disabled={disabled}
      />
      <CreateOrEditTirageHistory
        open={openCreateForm}
        onOpenChange={setOpenCreateForm}
        tirageId={tirageId}
        disabled={disabled}
      />
      <div className=" flex justify-between items-baseline">
        <label className="text-sm font-medium text-gray-700">
          {t("tiragePrintHistory")}
        </label>
        <AddButton type="button" onClick={() => setOpenCreateForm(true)} disabled={disabled} />
      </div>
      <DataTable
        enableDragRows
        onRowDragged={(d) => handleRowDragged(d.newData)}
        columns={datatableColumns}
        data={rows}
        onRowClick={(e) => handleUpdate(e.original)}
      />
    </div>
  );
};
